const ComponentRegistry = module.exports = {};
const FieldTypeUtils = require('./fieldTypes');
const FieldComponents = require('./fieldComponents');

const GENERIC_PRIMITIVE_COMPONENTS = FieldComponents.componentsByName;

const internals = {
    genericRegistry: null,
    registriesByFormId: {}
};

internals.isReactComponentObject = (Component) => !!Component
    && typeof Component === 'object'
    && !!Component.$$typeof;

internals.isRenderableComponent = (Component) => typeof Component === 'function'
    || typeof Component === 'string'
    || internals.isReactComponentObject(Component);

internals.getOwnExportValues = (Component) => {

    if (!Component || typeof Component !== 'object') {
        return [];
    }

    return [
        ...Object.getOwnPropertyNames(Component),
        ...Object.getOwnPropertySymbols(Component)
    ].map((key) => Component[key]);
};

internals.unwrapComponent = (Component) => {

    if (!Component || internals.isRenderableComponent(Component)) {
        return Component;
    }

    if (internals.isRenderableComponent(Component.default)) {
        return Component.default;
    }

    const renderableExport = internals.getOwnExportValues(Component).find(internals.isRenderableComponent);

    return renderableExport || Component;
};

internals.unwrapComponents = (components = {}) => Object.entries(components).reduce((collector, [key, Component]) => {

    collector[key] = internals.unwrapComponent(Component);
    return collector;
}, {});

internals.cloneComponentMap = (components = {}) => Object.defineProperties({},
    Object.getOwnPropertyDescriptors(components)
);

internals.mergeComponentMaps = (...componentMaps) => componentMaps.reduce((collector, componentMap = {}) => {

    Object.defineProperties(collector, Object.getOwnPropertyDescriptors(componentMap));
    return collector;
}, {});

internals.getFieldEventHandlers = (field = {}) => [
    'onBlur',
    'onChange',
    'onFocus'
].reduce((collector, handlerName) => {

    if (typeof field[handlerName] === 'function') {
        collector[handlerName] = field[handlerName];
    }

    return collector;
}, {});

internals.loadGenericPrimitiveComponents = () => internals.cloneComponentMap(GENERIC_PRIMITIVE_COMPONENTS);

internals.buildGenericRegistry = () => {

    const genericComponentsByType = internals.loadGenericPrimitiveComponents();
    const genericComponentsByName = internals.cloneComponentMap(genericComponentsByType);
    const registry = {
        componentsByKey: {},
        componentsByName: genericComponentsByName,
        componentsByType: genericComponentsByType,
        genericComponentsByType
    };

    Object.defineProperty(registry, 'defaultComponent', {
        configurable: true,
        enumerable: true,
        get() {
            return genericComponentsByType.TextField;
        }
    });

    return registry;
};

ComponentRegistry.getGenericRegistry = () => {

    if (!internals.genericRegistry) {
        internals.genericRegistry = internals.buildGenericRegistry();
    }

    return internals.genericRegistry;
};

ComponentRegistry.getRegistry = ({
    componentRegistry,
    formId
} = {}) => {

    if (componentRegistry) {
        return componentRegistry;
    }

    if (!formId) {
        return {};
    }

    return internals.registriesByFormId[formId] || {};
};

ComponentRegistry.register = ({
    formId,
    registry = {}
}) => {

    if (!formId) {
        return registry;
    }

    internals.registriesByFormId[formId] = registry;
    return registry;
};

ComponentRegistry.unregister = ({
    formId
}) => {

    if (!formId) {
        return;
    }

    delete internals.registriesByFormId[formId];
};

ComponentRegistry.buildRegistry = ({
    formId,
    components = {},
    componentsByKey = {},
    componentsByType = {},
    componentsByName = {},
    genericComponentsByType = {},
    defaultComponent,
    genericFieldAdapter,
    fieldAdapter
} = {}) => {

    const genericRegistry = ComponentRegistry.getGenericRegistry();
    const sameNameComponents = internals.unwrapComponents(components);

    const registry = {
        formId,
        componentsByKey: internals.mergeComponentMaps(
            genericRegistry.componentsByKey,
            internals.unwrapComponents(componentsByKey)
        ),
        componentsByType: internals.mergeComponentMaps(
            genericRegistry.componentsByType,
            sameNameComponents,
            internals.unwrapComponents(componentsByType)
        ),
        componentsByName: internals.mergeComponentMaps(
            genericRegistry.componentsByName,
            sameNameComponents,
            internals.unwrapComponents(componentsByName)
        ),
        genericComponentsByType: internals.mergeComponentMaps(
            genericRegistry.genericComponentsByType,
            internals.unwrapComponents(genericComponentsByType)
        ),
        genericFieldAdapter: internals.unwrapComponent(genericFieldAdapter || fieldAdapter)
    };

    Object.defineProperty(registry, 'defaultComponent', {
        configurable: true,
        enumerable: true,
        get() {
            return internals.unwrapComponent(defaultComponent) || genericRegistry.defaultComponent;
        }
    });

    return registry;
};

ComponentRegistry.isGenericFieldComponent = ({
    Component,
    genericComponentsByType = {}
}) => {

    const resolvedComponent = internals.unwrapComponent(Component);

    if (!resolvedComponent) {
        return false;
    }

    const componentName = resolvedComponent.displayName || resolvedComponent.name;

    if (componentName && Object.prototype.hasOwnProperty.call(genericComponentsByType, componentName)) {
        return internals.unwrapComponent(genericComponentsByType[componentName]) === resolvedComponent;
    }

    return Object.keys(genericComponentsByType).some((componentType) => internals.unwrapComponent(
        genericComponentsByType[componentType]
    ) === resolvedComponent);
};

internals.shouldUseFieldAdapter = ({
    field = {},
    Component,
    genericComponentsByType = {}
}) => {

    if (field.useFieldAdapter) {
        return true;
    }

    if (!field.name) {
        return ComponentRegistry.isGenericFieldComponent({
            Component,
            genericComponentsByType
        });
    }

    return !!field.component || ComponentRegistry.isGenericFieldComponent({
        Component,
        genericComponentsByType
    });
};

ComponentRegistry.resolveComponentType = (field = {}) => FieldTypeUtils.normalizePrimitiveType(field.componentType || field.type);

ComponentRegistry.getFieldComponent = ({
    field = {},
    componentRegistry,
    formId,
    componentsByKey,
    componentsByType,
    componentsByName,
    defaultComponent
}) => {

    const registry = ComponentRegistry.getRegistry({
        componentRegistry,
        formId
    });
    const resolvedComponentsByKey = componentsByKey || registry.componentsByKey || {};
    const resolvedComponentsByType = componentsByType || registry.componentsByType || {};
    const resolvedComponentsByName = componentsByName || registry.componentsByName || {};
    const componentType = ComponentRegistry.resolveComponentType(field);
    const resolvedComponent = (typeof field.component === 'function' || internals.isReactComponentObject(field.component) ? field.component : null)
        || resolvedComponentsByName[field.name]
        || resolvedComponentsByName[field.component]
        || resolvedComponentsByKey[field.componentKey]
        || resolvedComponentsByType[componentType]
        || resolvedComponentsByType[field.componentType || field.type]
        || FieldComponents.componentsByName[componentType]
        || FieldComponents.componentsByName[field.componentType || field.type];

    return internals.unwrapComponent(
        resolvedComponent
        || defaultComponent
        || registry.defaultComponent
    );
};

ComponentRegistry.buildFieldComponentProps = ({
    field = {},
    fieldProps = {},
    Component,
    componentRegistry,
    formId,
    genericComponentsByType
}) => {

    const registry = ComponentRegistry.getRegistry({
        componentRegistry,
        formId
    });
    const resolvedGenericComponentsByType = genericComponentsByType || registry.genericComponentsByType || {};
    const resolvedComponent = internals.unwrapComponent(Component);
    const propsForField = fieldProps[field.name] || {};

    const definitionComponentProps = field.componentProps || {};
    const {
        inputProps,
        textFieldProps,
        ...componentProps
    } = propsForField;
    const fieldComponentProps = {
        ...internals.getFieldEventHandlers(field),
        ...definitionComponentProps,
        ...componentProps
    };
    const definitionTextFieldProps = field.textFieldProps || {};

    const finalTextFieldProps = {
        ...definitionTextFieldProps,
        ...(textFieldProps || {}),
        helperText: textFieldProps?.helperText
            || definitionTextFieldProps.helperText
            || field.helperText,
        inputProps: {
            ...(definitionTextFieldProps.inputProps || {}),
            ...(textFieldProps?.inputProps || {}),
            ...(inputProps || {})
        }
    };

    if (internals.shouldUseFieldAdapter({
        field,
        Component: resolvedComponent,
        genericComponentsByType: resolvedGenericComponentsByType
    })) {
        return {
            Component: resolvedComponent,
            fieldName: field.name,
            field,
            ...fieldComponentProps,
            textFieldProps: finalTextFieldProps
        };
    }

    return {
        fieldName: field.name,
        field,
        ...fieldComponentProps,
        textFieldProps: finalTextFieldProps
    };
};

ComponentRegistry.resolveRenderableComponent = ({
    Component,
    componentRegistry,
    field = {},
    formId,
    genericFieldAdapter,
    genericComponentsByType
}) => {

    const registry = ComponentRegistry.getRegistry({
        componentRegistry,
        formId
    });

    const resolvedComponent = internals.unwrapComponent(Component);
    const resolvedGenericComponentsByType = genericComponentsByType || registry.genericComponentsByType || {};
    const resolvedGenericFieldAdapter = internals.unwrapComponent(genericFieldAdapter || registry.genericFieldAdapter);

    return internals.shouldUseFieldAdapter({
        field,
        Component: resolvedComponent,
        genericComponentsByType: resolvedGenericComponentsByType
    })
        ? resolvedGenericFieldAdapter
        : resolvedComponent;
};

ComponentRegistry.internals = internals;
